import { useCallback, useEffect } from "react";
import Pagination from "../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import { formatCurrencyForTable, formatDate } from "../../../lib/utils";
import {
  ExpenseItem,
  ExpenseItemSearchRequest,
  ExpenseItemSearchResponse,
} from "../@types";
import { useGetIncomeExpenseItemsMutation } from "../financeApiSlice";

interface IncomeDashTabProps {
  requestBody: ExpenseItemSearchRequest;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const IncomeDashTab: React.FC<IncomeDashTabProps> = ({
  requestBody,
  onPageChange,
  onPageSizeChange,
}) => {
  // RTK Query mutation
  const [getIncomeExpenseItems, { data: usersData, isLoading, error: loadError }] =
    useGetIncomeExpenseItemsMutation();

  const fetchExpenseItems = useCallback(async () => {
    try {
      await getIncomeExpenseItems(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch income items:", err);
    }
  }, [getIncomeExpenseItems, requestBody]);

  useEffect(() => {
    fetchExpenseItems();
  }, [fetchExpenseItems]);

  // Table columns configuration
  const columns: Column<ExpenseItem>[] = [
    {
      key: "tripNumber",
      header: "Trip number",
      width: "200px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "date",
      header: "Date",
      width: "200px",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
    {
      key: "expectedAmount",
      header: "Expected amount (NGN)",
      width: "200px",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "earnedAmount",
      header: "Earned amount (NGN)",
      width: "200px",
      render: (value: number) => (
        <span className="text-green-600">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "category",
      header: "Category",
      width: "200px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "description",
      header: "Description",
      width: "300px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    // {
    //   key: "expenseType",
    //   header: "Type",
    //   width: "150px",
    //   render: (value: string) => (
    //     <span
    //       className={cn(
    //         "px-2 py-1 text-[12px] font-semibold rounded-full",
    //         value === "income"
    //           ? "text-green-600 border border-green-500 bg-green-50"
    //           : "text-red-600 border border-red-500 bg-red-50"
    //       )}
    //     >
    //       {value}
    //     </span>
    //   ),
    // },
  ];

  const response = usersData?.data as ExpenseItemSearchResponse | undefined;

  return (
    <div className="overflow-hidden space-y-6">
      <Table
        data={response?.data || []}
        columns={columns}
        loading={isLoading}
        error={loadError}
        emptyMessage="No income items found"
      />

      {/* Pagination */}
      {response && (
        <Pagination
          currentPage={response.currentPage}
          totalPages={response.totalPages}
          pageSize={response.pageSize}
          totalCount={response.totalCount}
          hasNextPage={response.hasNextPage}
          hasPreviousPage={response.hasPreviousPage}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};

export default IncomeDashTab;
